const RECOVERY_MARKER = "cliqsuite:pwa-asset-recovery-attempted";
const PRECACHE_PREFIX = "workbox-precache";

function describe(reason: unknown): string {
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  if (reason && typeof reason === "object") {
    const value = reason as { name?: unknown; message?: unknown; errno?: unknown };
    return `${String(value.name ?? "")} ${String(value.message ?? "")} ${String(value.errno ?? "")}`;
  }
  return String(reason ?? "");
}

function isReleaseAssetFailure(reason: unknown): boolean {
  return /failed to fetch dynamically imported module|importing a module script failed|loading chunk|chunkloaderror|webassembly|wasm|errnoerror.*44/i.test(
    describe(reason),
  );
}

async function recoverOnce(): Promise<void> {
  try {
    if (sessionStorage.getItem(RECOVERY_MARKER)) return;
    sessionStorage.setItem(RECOVERY_MARKER, String(Date.now()));
  } catch {
    // Without a marker a persistent failure could create an infinite reload
    // loop, which is worse than leaving the original error visible.
    return;
  }

  // A precache miss means this release is already unable to run. Remove only
  // Workbox's code precache (never runtime caches, IndexedDB, localStorage, or
  // offline outboxes), ask the registration to re-check, and navigate again.
  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      await registration?.update();
    }
  } catch {
    // Network may still be unavailable; the reload below remains best effort.
  }
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.filter((name) => name.startsWith(PRECACHE_PREFIX)).map((name) => caches.delete(name)));
    }
  } catch {
    // Cache Storage may be unavailable in private/restricted browsing.
  }
  window.location.reload();
}

/**
 * One-shot recovery for a browser that loaded a shell from one release but
 * cannot load that release's hashed JS/WASM. This is an emergency fallback,
 * not the normal update path, and deliberately preserves all application data.
 */
export function installPwaAssetRecovery(): () => void {
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isReleaseAssetFailure(event.reason)) void recoverOnce();
  };
  const onError = (event: ErrorEvent) => {
    if (isReleaseAssetFailure(event.error ?? event.message)) void recoverOnce();
  };
  window.addEventListener("unhandledrejection", onUnhandledRejection);
  window.addEventListener("error", onError);
  return () => {
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    window.removeEventListener("error", onError);
  };
}
